import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import {
  EN,
  DE,
  FR,
  ES,
} from 'services/languages/constants'
import { getUserLanguagePrimary } from 'services/user'
import { MODAL_TYPES } from 'components/Modal'
import { TYPE_ANNOUNCEMENT_MODAL } from 'services/dialog'
import { get as getConfig } from 'config'
import { RESOLVER_TYPE_NOT_FOUND } from 'services/resolver/types'
import {
  isAccountPage,
  isSharePage,
  isPortal,
  isCart,
  isCartAccessDenied,
  isPlans,
  isGift,
  isGo,
  isGetStarted,
  isFullPlayer,
} from 'services/url'
import { isEntitled } from 'services/subscription'

const ANNOUNCEMENT_ACTIVATION_TIME = 2000

function getStaticText (language) {
  const staticText = {
    [EN]: {
      heading: 'Thank You',
      description: 'We appreciate you being a part of the Gaia community and the global evolution of consciousness.',
      button: 'Play Video',
    },
    [DE]: {
      heading: 'Dankeschön',
      description: 'Wir schätzen, dass du ein Teil unserer Gaia-Gemeinschaft bist und zur globalen Evolution des Bewusstseins beiträgst.',
      button: 'Video abspielen',
    },
    [FR]: {
      heading: 'Merci',
      description: 'Merci de contribuer à l\'éveil des consciences au sein de la communauté Gaia.',
      button: 'Lire la vidéo',
    },
    [ES]: {
      heading: '¡Gracias por ser parte de Gaia!',
      description: 'Gracias por ser un miembro valioso de Gaia. Tu apoyo nos permite crear contenido transformador que va más allá de lo ordinario, enriqueciendo tu mente, cuerpo y alma.',
      button: 'Ver ahora',
    },
  }
  const localizedText = staticText[language] || staticText[EN]
  return Map(localizedText)
}

function Announcement (props) {
  const {
    isTourActive,
    shouldHideAnnouncement,
    setFeatureTrackingDataPersistent,
    auth,
    language,
    renderModal,
    dismissModal,
    view,
    resolver,
  } = props

  const staticText = getStaticText(language)
  const { mediaId } = getConfig().features.announcementModal

  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))

  const shouldShowAnnouncement = useMemo(() => {
    const path = resolver.getIn(['data', 'path'])
    const query = resolver.getIn(['data', 'query'])
    /**
     * do not show announcement if user already seen it
     * or tour is active or interstitial is active
     * or not entitled
     */
    if (shouldHideAnnouncement || isTourActive || view || !isUserEntitled) {
      return false
    }
    /**
     * do not show announcemnt if the page is not found
     */
    if (resolver.getIn(['data', 'type']) === RESOLVER_TYPE_NOT_FOUND) {
      return false
    }
    /**
     * do not show announcemnt for the specific pages
     */
    if (
      isAccountPage(path)
      || isSharePage(path)
      || isPortal(path)
      || isCart(path)
      || isCartAccessDenied(path)
      || isPlans(path)
      || isGift(path)
      || isGo(path)
      || isGetStarted(path)
      || isFullPlayer(query)
    ) {
      return false
    }
    return true
  }, [
    resolver,
    shouldHideAnnouncement,
    isTourActive,
    view,
    isUserEntitled,
    language,
  ])

  const handleCloseClick = () => {
    handleModalDismiss()
    dismissModal(TYPE_ANNOUNCEMENT_MODAL)
  }

  const handleModalDismiss = () => {
    setFeatureTrackingDataPersistent({
      data: Map({ thankYouModal: true }),
      auth,
    })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowAnnouncement) {
        renderModal(TYPE_ANNOUNCEMENT_MODAL, {
          type: MODAL_TYPES.ROYAL_VIDEO,
          onDismiss: handleModalDismiss,
          onCloseClick: handleCloseClick,
          staticText,
          mediaId,
        })
      }
    }, ANNOUNCEMENT_ACTIVATION_TIME)
    return () => {
      clearTimeout(timer)
    }
  }, [shouldShowAnnouncement])

  return null
}

Announcement.propTypes = {
  shouldHideAnnouncement: PropTypes.bool,
  isTourActive: PropTypes.bool,
  language: PropTypes.string.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  view: PropTypes.string,
  checkoutComplete: PropTypes.bool,
}

export default connectRedux(
  (state) => ({
    shouldHideAnnouncement: state.featureTracking.getIn(['data', 'thankYouModal']),
    isTourActive: state.tour.get('isActive'),
    language: getUserLanguagePrimary(state.user) || EN,
    auth: state.auth,
    view: state.interstitial.get('view'),
    resolver: state.resolver,
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setFeatureTrackingDataPersistent: actions.featureTracking.setFeatureTrackingDataPersistent,
      renderModal: actions.dialog.renderModal,
      dismissModal: actions.dialog.dismissModal,
    }
  },
)(Announcement)
